import { DotSizesEnum } from '@/components/Dot'
import { TableDataInterface } from '@/lib/definitions/generic.types'
import { ReactElement } from 'react'
import { Location } from './api.types'
import { KNOWLEDGE_BASE_LINKS } from './constants'
import type { SupportTarget } from '@/lib/hooks/useSupportAction'

export interface ServiceItem extends TableDataInterface {
  id: string | number
  sid: any
  location: {
    name: string
    country: string
  }
  locations: {
    name: string
    country: string
  }[]
  name: {
    name: string
    id: string | number
  }
  url: string
  product: string
  project: string
  purchase_order: string
  startDate: number
  state: {
    style: string
    size?: DotSizesEnum
    title?: string
  }
  virtualNetworkName: string
  remote_customer: string
  is_flexpeer_remote_endpoint: boolean
  supportTarget: SupportTarget
}

export interface ServiceComponentItem extends TableDataInterface {
  id: string | number
  sco: any
  name: string
  component_name: string
  supportTarget: SupportTarget
}

export interface TicketItem extends TableDataInterface {
  ticket_number: number
  subject: {
    ticket_number: number
    subject: string
  }
  service: string
  type: {
    style?: string
    label: string
  }
  status: {
    style?: string
    label: string
  }
  escalation: {
    style?: string
    label: string
  }
  priority: {
    style?: string
    label: string
  }
  createdOn: string
  lastUpdatedOn: string
}

export interface SimpleDataItem {
  label: string | ReactElement
  value?: string | number | ReactElement
  className?: string
  labelClassName?: string
  valueClassName?: string
  copyToClipboard?: boolean
}

export enum ProductIdentifiers {
  PORT = 'PID47',

  // Internet Connectivity
  IP_TRANSIT = 'PID19',
  IP_TRANSIT_OVER_GRE = 'PID54',
  IP_ACCESS = 'PID20',
  LEGACY_IP_TRANSIT = 'PID10',
  LEGACY_IP_ACCESS = 'PID11',

  // Flex Ethernet
  FLEX_ETHERNET_ENDPOINT = 'PID44',
  LAYER2 = 'PID22',
  LEGACY_FLEX_ETHERNET = 'PID12',
  FLEX_PEER_ENDPOINT = 'PID53',

  // Cloud Connect
  CLOUD_CONNECT = 'PID25',

  // DDoS
  DDOS_PROTECTION = 'PID24',
  SE_DDOS_PROTECTION = 'PID18',

  // Extensions
  EXTENSION = 'PID48',

  // Wavelength
  WAVELENGTH = 'PID52',

  // Legacy Services:
  OFFICE_FIBER = 'PID21',
  CONSULTING = 'PID27',
  POP_AS_A_SERVICE = 'PID29',
  MANAGED_AS = 'PID30',
  IP_SALE_LEASE = 'PID46',
  SE_COLOCATION = 'PID13',
  SE_MANAGED_AS = 'PID14',
  SE_CLOUD_CONNECT = 'PID15',
  SE_OFFICE_FIBER = 'PID28',
  EN_COLOCATION = 'PID31',
  EN_ETHERNET = 'PID32',
  EN_CROSS_CONNECT = 'PID35',
  EN_METRO_FIBRE = 'PID39',
  EN_INTERNET = 'PID38',
  EN_LH_WAVE = 'PID40',
}

export enum ProductComponentIdentifiers {
  SETUP = 'PCO1',
  BILLING_GROUP = 'PC04',
  BURST = 'PCO5',
  PORT = 'PCO6',
  VLAN = 'PCO7',
  PREFIX = 'PCO8',
  BGP = 'PCO9',
  LAYER2 = 'PCO17',
  E_LAN = 'PCO27',
  IP_BANDWIDTH = 'PCO31',
  IP_BANDWIDTH_BURST = 'PCO32',
  ROUTED_PREFIX = 'PCO33',
  LAG = 'PCO10',
}

export type StatusStyle = 'success' | 'neutral' | 'warning' | 'danger'

export type ServiceStatus = 'Live' | 'Down' | 'Degraded'

export interface PortAlert {
  sco: string
  name: string
  lastActivity: string
  offlineDuration: string
  location: Location
  reason: string
  trafficMetrics?: {
    lastKnownBandwidth: number
    averageUtilization: number
    lastPeakTime: string
    errorsReceived: number
    errorsTransmitted: number
    discardReceived: number
    discardTransmitted: number
  }
  affectedServices?: {
    sid: string
    name: string
    location: Location
  }[]
}

export interface AlertStats {
  errors: number
  discards: number
}

export interface AlertData {
  // existing alert data properties...
  stats?: AlertStats
}

export const DashboardTabs: Record<string, string> = {
  PORTS: 'ports',
  FLEX_ETHERNET: 'flex-ethernet',
  WAVELENGTH: 'wavelength',
  INTERNET_CONNECTIVITY: 'internet-connectivity',
  DDOS_PROTECTION: 'ddos-protection',
  EXTENSIONS: 'extensions',
  LEGACY_SERVICES: 'legacy-services',
}

type DashboardTabsProducts = Record<
  (typeof DashboardTabs)[keyof typeof DashboardTabs],
  (ProductIdentifiers | ProductComponentIdentifiers)[]
>

export const DashboardTabsProducts: DashboardTabsProducts = {
  [DashboardTabs.PORTS]: [
    ProductComponentIdentifiers.PORT,
    ProductComponentIdentifiers.LAG,
  ],
  [DashboardTabs.FLEX_ETHERNET]: [
    ProductIdentifiers.FLEX_ETHERNET_ENDPOINT,
    ProductIdentifiers.LAYER2,
    ProductIdentifiers.LEGACY_FLEX_ETHERNET,
    ProductIdentifiers.FLEX_PEER_ENDPOINT,
  ],
  [DashboardTabs.INTERNET_CONNECTIVITY]: [
    ProductIdentifiers.IP_TRANSIT,
    ProductIdentifiers.IP_TRANSIT_OVER_GRE,
    ProductIdentifiers.IP_ACCESS,
    ProductIdentifiers.LEGACY_IP_TRANSIT,
    ProductIdentifiers.LEGACY_IP_ACCESS,
  ],
  [DashboardTabs.DDOS_PROTECTION]: [
    ProductIdentifiers.DDOS_PROTECTION,
    ProductIdentifiers.SE_DDOS_PROTECTION,
  ],
  [DashboardTabs.EXTENSIONS]: [ProductIdentifiers.EXTENSION],
  [DashboardTabs.WAVELENGTH]: [ProductIdentifiers.WAVELENGTH],
  [DashboardTabs.LEGACY_SERVICES]: [
    ProductIdentifiers.OFFICE_FIBER,
    ProductIdentifiers.CONSULTING,
    ProductIdentifiers.POP_AS_A_SERVICE,
    ProductIdentifiers.MANAGED_AS,
    ProductIdentifiers.IP_SALE_LEASE,
    ProductIdentifiers.SE_COLOCATION,
    ProductIdentifiers.SE_MANAGED_AS,
    ProductIdentifiers.SE_CLOUD_CONNECT,
    ProductIdentifiers.SE_OFFICE_FIBER,
    ProductIdentifiers.EN_COLOCATION,
    ProductIdentifiers.EN_ETHERNET,
    ProductIdentifiers.EN_CROSS_CONNECT,
    ProductIdentifiers.EN_METRO_FIBRE,
    ProductIdentifiers.EN_INTERNET,
    ProductIdentifiers.EN_LH_WAVE,
  ],
}

type KnowledgeBaseUrls = Record<
  string,
  (ProductIdentifiers | ProductComponentIdentifiers | string)[]
>

export const KnowledgeBaseUrls: KnowledgeBaseUrls = {
  [KNOWLEDGE_BASE_LINKS.PORTS]: [
    ProductComponentIdentifiers.PORT,
    ProductComponentIdentifiers.LAG,
  ],
  [KNOWLEDGE_BASE_LINKS.FLEX_ETHERNET]: [
    ProductIdentifiers.FLEX_ETHERNET_ENDPOINT,
    ProductIdentifiers.LAYER2,
    ProductIdentifiers.LEGACY_FLEX_ETHERNET,
    ProductIdentifiers.FLEX_PEER_ENDPOINT,
  ],
  [KNOWLEDGE_BASE_LINKS.IP_TRANSIT]: [
    ProductIdentifiers.IP_TRANSIT,
    ProductIdentifiers.LEGACY_IP_TRANSIT,
  ],
  [KNOWLEDGE_BASE_LINKS.IP_ACCESS]: [
    ProductIdentifiers.IP_ACCESS,
    ProductIdentifiers.LEGACY_IP_ACCESS,
  ],
  [KNOWLEDGE_BASE_LINKS.DDOS_PROTECTION]: [
    ProductIdentifiers.DDOS_PROTECTION,
    ProductIdentifiers.SE_DDOS_PROTECTION,
  ],
  [KNOWLEDGE_BASE_LINKS.FLEX_TUNNEL]: [ProductIdentifiers.IP_TRANSIT_OVER_GRE],
  [KNOWLEDGE_BASE_LINKS.FLOWSPEC]: ['bgpflowspec'],
}

/**
 * Port type
 */
export enum PortType {
  IXP = 'IXP',
  PNI = 'PNI',
}

export const PortTypeMap: Record<PortType, { label: string; value: PortType }> =
  {
    [PortType.IXP]: {
      label: 'IXP',
      value: PortType.IXP,
    },
    [PortType.PNI]: {
      label: 'PNI',
      value: PortType.PNI,
    },
  }

export const getProductKnowledgeBaseUrl = (
  productIdentifier: ProductIdentifiers | ProductComponentIdentifiers
) => {
  // get the key of the KnowledgeBaseUrls object that contains the productIdentifier
  const knowledgeBaseUrl = Object.keys(KnowledgeBaseUrls).find((key) =>
    KnowledgeBaseUrls[key].includes(productIdentifier)
  )
  if (!knowledgeBaseUrl) {
    return undefined
  }
  return knowledgeBaseUrl
}
